import { z } from 'zod';
import type { AdoptionFormSchema, FormField } from '../types';

export function generateZodSchema(schema: AdoptionFormSchema) {
  const shape: Record<string, z.ZodTypeAny> = {};

  schema.sections.forEach((section) => {
    section.fields.forEach((field) => {
      let zodField: z.ZodTypeAny;

      switch (field.type) {
        case 'email':
          zodField = z.string().email('Email inválido');
          break;
        case 'number':
          zodField = z.number({ invalid_type_error: 'Debe ser un número' });
          if (field.validation?.min !== undefined) {
            zodField = (zodField as z.ZodNumber).min(field.validation.min, `Mínimo ${field.validation.min}`);
          }
          if (field.validation?.max !== undefined) {
            zodField = (zodField as z.ZodNumber).max(field.validation.max, `Máximo ${field.validation.max}`);
          }
          break;
        case 'checkbox':
          zodField = z.boolean();
          break;
        case 'date':
          zodField = z.string().refine((val) => !isNaN(Date.parse(val)), {
            message: 'Fecha inválida',
          });
          break;
        case 'range':
          // Range pode ser um array [min, max] ou um número único dependendo da implementação
          // Aqui assumimos array de números para range slider
          zodField = z.array(z.number());
          break;
        case 'city':
        case 'lookup':
          // City e Lookup se comportam como select (ID numérico ou string dependendo da implementação)
          // Geralmente IDs são números, mas select value é string no HTML
          zodField = z.union([z.string(), z.number()]);
          break;
        case 'radio':
          // Radio buttons enviam strings "true"/"false" ou outros valores
          zodField = z.string({ invalid_type_error: 'Debe ser una cadena de texto' });
          break;
        default:
          zodField = z.string({ invalid_type_error: 'Debe ser una cadena de texto' });
          if (field.validation?.minLength) {
            zodField = (zodField as z.ZodString).min(field.validation.minLength, `Mínimo ${field.validation.minLength} caracteres`);
          }
          if (field.validation?.maxLength) {
            zodField = (zodField as z.ZodString).max(field.validation.maxLength, `Máximo ${field.validation.maxLength} caracteres`);
          }
          if (field.validation?.pattern) {
            zodField = (zodField as z.ZodString).regex(new RegExp(field.validation.pattern), field.validation.message || 'Formato inválido');
          }
          break;
      }

      if (!field.required) {
        zodField = zodField.optional().or(z.literal(''));
      } else {
        // Para campos obrigatórios, adicionar validação apropriada por tipo
        if (field.type === 'text' || field.type === 'textarea' || field.type === 'email' || field.type === 'tel') {
          zodField = (zodField as z.ZodString).min(1, 'Este campo es obligatorio');
        } else if (field.type === 'radio') {
          // Radio buttons obrigatórios devem ter um valor selecionado
          zodField = (zodField as z.ZodString).min(1, 'Debes seleccionar una opción');
        } else if (field.type === 'city' || field.type === 'lookup' || field.type === 'select') {
          // Selects, city e lookup obrigatórios devem ter um valor
          zodField = z.union([
            z.string().min(1, 'Este campo es obligatorio'),
            z.number().refine((val) => val !== undefined && val !== null, 'Este campo es obligatorio')
          ]);
        }
      }

      shape[field.id] = zodField;
    });
  });

  return z.object(shape);
}
